import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { IStore } from "../../../../redux/store";
import { portCallsTabData } from "redux/app/vdmdashboard/data";
import SVGWarn from "components/_common/icons/vdm/SVGWarn";
import classNames from "classnames";
import vdmDashboardSelectors from "redux/app/vdmdashboard/selectors";
import { Tooltip } from "react-tippy";
import { IVDMDashboardContent } from "redux/app/vdmdashboard/@types";

function getOpsTypeStyle(color: string): React.CSSProperties {
	switch (color) {
		case "yellow":
			return { backgroundColor: "#FEF9C3", color: "#B45309" }; // yellow-100 bg, yellow-700 text
		case "blue":
			return { backgroundColor: "#DBEAFE", color: "#1D4ED8" }; // blue-100 bg, blue-700 text
		case "purple":
			return { backgroundColor: "#EDE9FE", color: "#7C3AED" }; // purple-100 bg, purple-700 text
		case "gray":
			return { backgroundColor: "#D1D5DB", color: "#374151" }; // gray-300 bg, gray-700 text
		default:
			return {};
	}
}

function getPortTagStyle(color: string): React.CSSProperties {
	switch (color) {
		case "gray":
			return { backgroundColor: "#E5E7EB", color: "#374151" }; // gray-200 bg, gray-700 text
		case "red":
			return { backgroundColor: "#FEE2E2", color: "#991B1B" }; // red-100 bg, red-800 text
		case "orange":
			return { backgroundColor: "#FFEDD5", color: "#9A3412" }; // orange-100 bg, orange-800 text
		case "green":
			return { backgroundColor: "#DCFCE7", color: "#166534" }; // green-100 bg, green-800 text
		default:
			return {};
	}
}

function getPortTagInlineStyle(color: string): React.CSSProperties {
	switch (color) {
		case "gray":
			return { backgroundColor: "#e5e7eb", color: "#374151" }; // bg-gray-200, text-gray-700
		case "red":
			return { backgroundColor: "#d72121", color: "#991b1b" }; // bg-red-100, text-red-800
		case "orange":
			return { backgroundColor: "#ffedd5", color: "#9a3412" }; // bg-orange-100, text-orange-800
		case "green":
			return { backgroundColor: "#2fb960", color: "#147e3d" }; // bg-green-100, text-green-800
		default:
			return {};
	}
}

const reportTypeTitleMapper = {
	LAST_PORT_CALL: "Last Port Call",
};

const statusLabelColorMap: Record<string, string> = {
	red: "bg-red-100 text-red-800",
	green: "bg-green-100 text-green-800",
};

const formatDate = (isoDate: string | null) => {
	if (!isoDate) return "-";
	const dateObj = new Date(isoDate);
	if (isNaN(dateObj.getTime())) return isoDate;

	// Format as "08 Oct" or "23 Mar 2026"
	const options: Intl.DateTimeFormatOptions = {
		day: "numeric",
		month: "short",
	};
	return dateObj.toLocaleDateString("en-US", options);
};

const getTypeTag = (type: string) => {
	if (type === "LAST_PORT_CALL")
		return [{ label: "Last Port Call", color: "gray" }];
	if (type === "DEPARTURE_UPCOMING")
		return [{ label: "Upcoming Departure", color: "orange" }];
	if (type === "ARRIVAL_UPCOMING")
		return [{ label: "Upcoming Arrival", color: "green" }];
	return []; // No tag for others or default logic
};

type OwnProps = {
	vesselId: string;
};
type VDMDashboardPortCallsProps = PropsFromRedux & OwnProps;
function VDMDashboardPortCalls(props: VDMDashboardPortCallsProps) {
	const { portCalls } = props;

	return (
		<div className="mt-4 rounded-md overflow-x-auto border">
			<table className="min-w-full  text-sm">
				<thead>
					<tr className="bg-gray-100 text-left">
						<th className="p-2 px-3 sticky left-0 z-10 bg-gray-100 border-r w-44 min-w-[16rem] max-w-xs text-sm font-medium whitespace-nowrap">
							Port Name
						</th>
						<th className="p-2 px-3 text-sm font-medium whitespace-nowrap">
							Berth Name
						</th>
						<th className="p-2 px-3 text-sm font-medium whitespace-nowrap">
							ETA
						</th>
						<th className="p-2 px-3 text-sm font-medium whitespace-nowrap">
							ETB
						</th>
						<th className="p-2 px-3 text-sm font-medium whitespace-nowrap">
							ETD
						</th>
						<th className="p-2 px-3 text-sm font-medium whitespace-nowrap">
							Ops Type
						</th>
						<th className="p-2 px-3 text-sm font-medium whitespace-nowrap">
							Cargo Type
						</th>
						<th className="p-2 px-3 text-sm font-medium whitespace-nowrap">
							Activity Type
						</th>
						<th className="p-2 px-3 text-sm font-medium whitespace-nowrap">
							Cargo Qty.
						</th>
						<th className="p-2 px-3 text-sm font-medium whitespace-nowrap">
							Remarks
						</th>
						<th className="p-2 px-3 text-sm font-medium whitespace-nowrap">
							Owners Agent
						</th>
					</tr>
				</thead>
				<tbody>
					{!portCalls || portCalls.length === 0 ? (
						<tr>
							<td colSpan={11} className="p-4 text-center text-gray-500">
								No port calls data available.
							</td>
						</tr>
					) : (
						portCalls.map((row, i) => {
							const etaFormatted = formatDate(row.eta);
							const etbFormatted = formatDate(row.etb);
							const etdFormatted = formatDate(row.etd);

							return (
								<tr key={i} className="even:bg-gray-50">
									<td className="sticky left-0 z-10 bg-white border-r w-44  min-w-[16rem] max-w-xs text-sm border-b">
										<div
											className={classNames(
												"flex items-center gap-x-1 p-2 px-3 py-3",
												{
													"bg-gradient-to-r from-blue-500 via-white to-white":
														i === 0,
												},
											)}
										>
											<span
												className="truncate font-medium min-w-0 max-w-[150px] 3xl:max-w-none"
												title={row.portName}
											>
												{row.portName}
											</span>
											<div className="flex flex-wrap gap-1  flex-1 min-w-0">
												{getTypeTag(row.reportType)?.map((tag, j) => (
													<span
														key={j}
														style={getPortTagStyle(tag.color)}
														className="px-2 py-0.5 rounded-full text-xs font-medium flex items-center"
													>
														<SVGWarn className="w-4 h-3 mt-0.5 mr-0.5 mb-[3px]" />
														<span>{tag.label}</span>
													</span>
												))}
											</div>
										</div>
									</td>
									<td className="p-2 px-3 py-3 text-sm whitespace-nowrap border-b text-center">
										{/* {row.berthName} */}
										NA
									</td>

									<td className="p-2 px-3 py-3 text-sm whitespace-nowrap border-b">
										<div className="flex flex-col gap-1">
											{/* INFO: Not part of phase-1 */}
											{/* {row?.etaType ? (
											row?.etaType === 1 ? (
												<span className="flex items-center gap-x-1">
													<span className="text-gray-500 line-through">
														{row?.actualEta}
													</span>{" "}
													<span className="text-red-500">»</span>{" "}
													<span>{row?.eta}</span>
												</span>
											) : row?.etaType === -1 ? (
												<span className="flex items-center gap-x-1">
													<span>{row?.eta}</span>{" "}
													<span className="text-green-500">«</span>{" "}
													<span className="text-gray-500 line-through">
														{row?.actualEta}
													</span>
												</span>
											) : null
										) : (
											<span>{row.eta}</span>
										)}
										{row.etaStatus && (
											<div className="flex items-center">
												<span
													className="h-1.5 w-1.5 rounded-full flex items-center mr-1"
													style={getPortTagInlineStyle(row?.etaStatus?.color)}
												/>
												<span
													className={
														"text-xs rounded-full px-2 " +
														(row.etaStatus.color
															? statusLabelColorMap[row.etaStatus.color]
															: "")
													}
												>
													{row?.etaStatus?.label}
												</span>
											</div>
										)} */}

											<span>{etaFormatted}</span>
										</div>
									</td>

									<td className="p-2 px-3 py-3 text-sm whitespace-nowrap border-b text-center">
										{etbFormatted}
									</td>

									<td className="p-2 px-3 py-3 text-sm whitespace-nowrap border-b">
										<div className="flex flex-col gap-1">
											{/* INFO: Not part of phase-1 */}
											{/* {row?.etdType ? (
											row?.etdType === 1 ? (
												<span className="flex items-center gap-x-1">
													<span className="text-gray-500 line-through">
														{row?.actualEtd}
													</span>{" "}
													<span className="text-red-500">»</span>{" "}
													<span>{row?.etd}</span>
												</span>
											) : row?.etdType === -1 ? (
												<span className="flex items-center gap-x-1">
													<span>{row?.eta}</span>{" "}
													<span className="text-green-500">«</span>{" "}
													<span className="text-gray-500 line-through">
														{row?.actualEtd}
													</span>
												</span>
											) : null
										) : (
											<span>{row.etd}</span>
										)}
										{row.etdStatus && (
											<div className="flex items-center">
												<span
													className="h-1.5 w-1.5 rounded-full flex items-center mr-1"
													style={getPortTagInlineStyle(row?.etdStatus?.color)}
												/>
												<span
													className={
														"text-xs rounded-full px-2 " +
														(row.etdStatus.color
															? statusLabelColorMap[row.etdStatus.color]
															: "")
													}
												>
													{row?.etdStatus?.label}
												</span>
											</div>
										)} */}
											<span>{etdFormatted}</span>
										</div>
									</td>
									<td className="p-2 px-3 py-3 text-sm whitespace-nowrap border-b text-center">
										<span
											// style={getOpsTypeStyle(row.opsTypeColor)}
											className={
												"px-2 py-1 rounded-full font-bold text-xs inline-block "
											}
										>
											{/* {row.opsType} */}-
										</span>
									</td>
									<td className="p-2 px-3 py-3 text-sm whitespace-nowrap border-b">
										{/* {row.cargoType} */} -
									</td>
									<td className="p-2 px-3 py-3 text-sm whitespace-nowrap border-b">
										{row?.activityType && row.activityType.length > 0 ? (
											<Tooltip
												html={<>{row.activityType.join(", ")}</>}
												position="bottom"
												trigger="mouseenter"
												arrow
												theme="light"
												className="w-full"
											>
												<div className="flex gap-1 cursor-default">
													<span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-lg text-xs font-medium">
														{row.activityType[0]}
													</span>
													{row.activityType.length > 1 && (
														<span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded-lg text-xs">
															+{row.activityType.length - 1}
														</span>
													)}
												</div>
											</Tooltip>
										) : null}
									</td>
									<td className="p-2 px-3 py-3 text-sm whitespace-nowrap border-b text-center">
										{/* {row.cargoQty} */} -
									</td>
									<td className="p-2 px-3 py-3 text-sm whitespace-nowrap border-b">
										<div className="flex gap-1 items-center">
											{/* {row.remarks?.map((rem, k) => (
											<span key={k} title={rem.tooltip || ""}>
												{rem.icon}
											</span>
										))} */}
										</div>
									</td>
									<td className="p-2 px-3 py-3  text-sm whitespace-nowrap border-b text-center">
										{/* {row.ownersAgent} */}-
										{/* {row.ownersAgentIcon && <span>{row.ownersAgentIcon}</span>} */}
									</td>
								</tr>
							);
						})
					)}
				</tbody>
			</table>
		</div>
	);
}
function mapStateToProps(store: IStore, ownProps: OwnProps) {
	const vdmDashboardStore = vdmDashboardSelectors._getStore(store);
	const vdmDashboardContent =
		vdmDashboardSelectors.getVDMDashboardContent(vdmDashboardStore);
	const portCalls = vdmDashboardContent?.portCalls;
	return {
		portCalls,
	};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(VDMDashboardPortCalls);
